/**
 * @fileoverview Fill a class from the roster that already exists in a sheet.
 *
 * **The preview is the contract**, the same as the schedule builder's: adding
 * thirty students and finding out afterwards that four were wrong leaves a
 * roster nobody can trust, so every row is resolved and shown first and the
 * same resolution runs on apply. What you approve is what lands.
 *
 * Each row says what will happen to it: a student the instructor already
 * teaches is MATCHED and only enrolled, a new name is CREATED. An import never
 * rewrites an existing student — not their name, not their address, and above
 * all not a consent state their family already answered.
 *
 * **Importing is not consent, and the dialog says so out loud.** Students
 * created here land with no consent and cannot be recorded until a real grant
 * arrives. A roster handed over by a school is a list of names; the request to
 * the family is still a separate, deliberate act.
 */

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Icon, Tag } from "@/components/studio/primitives";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 " +
  "font-sans text-[14px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

/**
 * `endpoint` defaults to the administrator's import. A program viewer with the
 * `roster_upload` grant passes theirs (`/org/viewer/classes/:id/roster/import`),
 * which runs the very same import on the server.
 */
export default function ImportRosterDialog({ open, onOpenChange, classId, className, onImported, endpoint }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  // Reset between openings so a dialog never reopens holding a stale preview.
  useEffect(() => {
    if (!open) { setText(""); setPreview(null); }
  }, [open]);

  /** Any edit invalidates the preview — approving a stale list is the bug this
      whole screen exists to prevent. */
  const onText = (e) => { setText(e.target.value); setPreview(null); };

  const run = async (apply) => {
    if (!text.trim()) {
      toast.error("Paste the roster, or choose a file");
      return;
    }
    setBusy(true);
    try {
      const res = await orgApi.post(endpoint || `/org/classes/${classId}/roster/import`, { text, apply });
      setPreview(res.data);
      if (apply) {
        const { created = 0, matched = 0 } = res.data?.counts || {};
        toast.success(
          `${created + matched} student${created + matched === 1 ? "" : "s"} enrolled` +
            (created ? ` · ${created} new` : ""),
        );
        onImported?.(res.data);
        onOpenChange(false);
      }
    } catch (err) {
      setPreview(null);
      toast.error(getOrgErrorMessage(err, "Couldn't read that roster"));
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 1_000_000) {
      toast.error("That file is too large to be a class roster");
      return;
    }
    setText(await file.text());
    setPreview(null);
  };

  const counts = preview?.counts;
  const rows = preview?.students || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">
            Add students to {className || "this class"}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
          <span className="font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
            One student per line
          </span>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            data-testid="roster-pick-file"
            className="inline-flex items-center gap-1 font-sans text-[13px] font-medium text-[var(--accent-yellow-text)] hover:underline"
          >
            <Icon name="plus" size={14} /> Choose a file
          </button>
        </div>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.tsv,.txt,text/csv,text/plain"
          onChange={onFile}
          data-testid="roster-file"
          className="hidden"
        />

        <textarea
          value={text}
          onChange={onText}
          rows={7}
          placeholder={"Ada, Lovelace, ada@example.com\nGrace, Hopper\nAlan Turing, alan@example.com"}
          aria-label="Roster"
          data-testid="roster-text"
          className={inputClass}
        />
        <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
          First name, last name, and an email if you have one — a header row is ignored. Paste straight out of a
          spreadsheet or drop in a CSV.
        </p>

        <p className="mt-3 rounded-[12px] border border-[var(--studio-divider)] px-3 py-2 font-sans text-[12px] text-[var(--text-secondary)]">
          Adding students here isn&rsquo;t consent. Everyone imported starts with none, so nothing can be
          recorded for them until their family says yes.
        </p>

        {preview && (
          <div className="mt-4" data-testid="roster-preview">
            <div className="flex flex-wrap items-center gap-2">
              <Tag accent>{counts.created} new</Tag>
              {counts.matched > 0 && <Tag>{counts.matched} already yours</Tag>}
              {counts.already_enrolled > 0 && <Tag>{counts.already_enrolled} already in the class</Tag>}
              {counts.skipped > 0 && <Tag>{counts.skipped} listed twice</Tag>}
            </div>

            {(preview.problems || []).length > 0 && (
              // Reported with its line, never dropped: a roster quietly one
              // short is worse than one that says what it could not read.
              <p data-testid="roster-problems" className="mt-2 font-sans text-[13px] text-[var(--state-warning)]">
                {preview.problems.slice(0, 4).map((p) => `Line ${p.line}: ${p.reason}`).join(" · ")}
              </p>
            )}

            <div className="mt-3 max-h-56 overflow-y-auto rounded-[12px] border border-[var(--studio-divider)]">
              {rows.map((r) => (
                <div
                  key={`${r.line}-${r.first_name}-${r.last_name}`}
                  className="flex items-center justify-between gap-3 border-b border-[var(--studio-divider)] px-3 py-1.5 last:border-b-0"
                >
                  <span className="min-w-0 truncate font-sans text-[14px] text-[var(--text-primary)]">
                    {[r.first_name, r.last_name].filter(Boolean).join(" ")}
                    {r.email && <span className="ml-2 text-[12px] text-[var(--text-tertiary)]">{r.email}</span>}
                  </span>
                  <span className="shrink-0">
                    {r.enrolled ? <Tag>Already in the class</Tag> : r.match === "existing" ? <Tag>Already yours</Tag> : <Tag accent>New</Tag>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button variant="ghost" onPress={() => run(false)} disabled={busy} data-testid="roster-preview-btn">
            {busy ? "Working…" : "Check the list"}
          </Button>
          {preview && (
            <Button onPress={() => run(true)} disabled={busy} data-testid="roster-apply">
              Add these {counts.created + counts.matched}
            </Button>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
